// File: frontend/src/pages/Analytics/AnalyticsPage.jsx
// Simplified analytics — 5 honest business questions, no tabs, no debug

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';

const currencyBR = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});

const STAGE_LABELS = {
  lead: 'Lead',
  qualified: 'Qualificado',
  proposal: 'Proposta',
  negotiation: 'Negociacao',
};

// ---- Small layout primitives ----

const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{children}</h3>
);

const BigNumber = ({ value, className = '' }) => (
  <p className={`text-4xl font-bold text-gray-900 dark:text-gray-100 mb-1 ${className}`}>{value}</p>
);

// Pulse skeleton for loading state
const Skeleton = ({ h = 'h-6', w = 'w-full' }) => (
  <div className={`${h} ${w} bg-gray-200 dark:bg-gray-700 rounded animate-pulse`} />
);

const LoadingCard = () => (
  <Card>
    <Skeleton h="h-5" w="w-48" />
    <div className="mt-4 space-y-3">
      <Skeleton h="h-10" w="w-24" />
      <Skeleton h="h-4" w="w-full" />
      <Skeleton h="h-4" w="w-3/4" />
    </div>
  </Card>
);

// ---- Section 1: Leads entered ----
const LeadsSection = ({ data }) => {
  const { total, by_source } = data;
  const max = by_source.length > 0 ? Math.max(...by_source.map((s) => s.count)) : 1;

  return (
    <Card>
      <SectionTitle>Quantos leads entraram?</SectionTitle>
      <BigNumber value={total} />
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">leads no periodo</p>
      {by_source.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">Sem leads no periodo.</p>
      ) : (
        <ul className="space-y-2">
          {by_source.map((s) => (
            <li key={s.source} className="flex items-center gap-3">
              <span className="w-36 text-sm text-gray-600 dark:text-gray-300 truncate shrink-0">
                {s.source || 'Desconhecido'}
              </span>
              <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-blue-500 h-3 rounded-full"
                  style={{ width: `${Math.round((s.count / max) * 100)}%` }}
                />
              </div>
              <span className="w-8 text-right text-sm font-medium text-gray-700 dark:text-gray-200 shrink-0">
                {s.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

// ---- Section 2: HSM sent ----
const HsmSection = ({ data }) => (
  <Card>
    <SectionTitle>Quantos HSM T0 foram enviados?</SectionTitle>
    <BigNumber value={data.sent} />
    <p className="text-sm text-gray-500 dark:text-gray-400">
      mensagens HSM disparadas no periodo.
    </p>
    <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
      Resposta por lead: veja o painel "Conversa WhatsApp" na ficha de cada lead.
    </p>
  </Card>
);

// ---- Section 3: Pipeline ----
const PipelineSection = ({ data }) => {
  const { stages, total_value } = data;

  return (
    <Card>
      <SectionTitle>O que esta vivo no pipeline?</SectionTitle>
      <BigNumber value={currencyBR.format(total_value)} />
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">valor total em aberto</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4 italic">
        Snapshot atual (nao filtrado por periodo).
      </p>
      {stages.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">Sem oportunidades abertas.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className="text-left py-2 font-medium text-gray-600 dark:text-gray-300">Estagio</th>
                <th className="text-right py-2 font-medium text-gray-600 dark:text-gray-300">Oportunidades</th>
                <th className="text-right py-2 font-medium text-gray-600 dark:text-gray-300">Valor</th>
              </tr>
            </thead>
            <tbody>
              {stages.map((row) => (
                <tr key={row.stage} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-2 text-gray-700 dark:text-gray-200">
                    {STAGE_LABELS[row.stage] || row.stage}
                  </td>
                  <td className="py-2 text-right text-gray-700 dark:text-gray-200">{row.count}</td>
                  <td className="py-2 text-right text-gray-700 dark:text-gray-200">
                    {currencyBR.format(row.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ---- Section 4: Closed deals ----
const ClosedSection = ({ data }) => {
  const { won, lost } = data;

  return (
    <Card>
      <SectionTitle>O que ganhamos e perdemos?</SectionTitle>
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400 mb-1">
            Ganhas
          </p>
          <p className="text-3xl font-bold text-green-700 dark:text-green-300">{won.count}</p>
          <p className="text-sm text-green-600 dark:text-green-400">{currencyBR.format(won.value)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">
            Perdidas
          </p>
          <p className="text-3xl font-bold text-red-700 dark:text-red-300">{lost.count}</p>
          <p className="text-sm text-red-600 dark:text-red-400">{currencyBR.format(lost.value)}</p>
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Motivos de perda</p>
        {lost.reasons.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">Sem perdas no periodo.</p>
        ) : (
          <ul className="space-y-1">
            {lost.reasons.map((r) => (
              <li key={r.reason} className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
                <span>{r.reason || 'Nao informado'}</span>
                <span className="font-medium">{r.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

// ---- Section 5: Activities by user ----
const ActivitiesSection = ({ data }) => {
  if (data.length === 0) {
    return (
      <Card>
        <SectionTitle>Quem fez o que?</SectionTitle>
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">Sem atividades no periodo.</p>
      </Card>
    );
  }

  // Collect all unique activity types
  const types = [...new Set(data.map((r) => r.type))].sort();

  // Pivot: user -> { type -> count }
  const byUser = {};
  for (const row of data) {
    if (!byUser[row.user_name]) byUser[row.user_name] = {};
    byUser[row.user_name][row.type] = (byUser[row.user_name][row.type] || 0) + row.count;
  }

  const users = Object.keys(byUser).sort();

  return (
    <Card>
      <SectionTitle>Quem fez o que?</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-600">
              <th className="text-left py-2 font-medium text-gray-600 dark:text-gray-300">Usuario</th>
              {types.map((t) => (
                <th key={t} className="text-right py-2 font-medium text-gray-600 dark:text-gray-300 capitalize">
                  {t}
                </th>
              ))}
              <th className="text-right py-2 font-medium text-gray-600 dark:text-gray-300">Total</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const row = byUser[user];
              const total = types.reduce((sum, t) => sum + (row[t] || 0), 0);
              return (
                <tr key={user} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-2 text-gray-700 dark:text-gray-200">{user}</td>
                  {types.map((t) => (
                    <td key={t} className="py-2 text-right text-gray-600 dark:text-gray-300">
                      {row[t] || '-'}
                    </td>
                  ))}
                  <td className="py-2 text-right font-semibold text-gray-700 dark:text-gray-200">{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ---- Main page ----

const AnalyticsPage = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, error } = useQuery(
    ['analytics-simple', days],
    () => api.get(`/analytics/simple?days=${days}`).then((res) => res.data.data),
    {
      retry: 1,
      staleTime: 60 * 1000,
    }
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            As 5 perguntas do negocio, com numeros verificados
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          className="block pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
        >
          <option value={30}>Ultimos 30 dias</option>
          <option value={90}>Ultimos 90 dias</option>
          <option value={365}>Ultimo ano</option>
        </select>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800 dark:text-red-300">Erro ao carregar dados</p>
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {error.message || 'Falha na conexao com o servidor. Tente recarregar a pagina.'}
          </p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LoadingCard />
            <LoadingCard />
          </div>
          <LoadingCard />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LoadingCard />
            <LoadingCard />
          </div>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && data && (
        <div className="space-y-6">
          {/* Row 1: Leads + HSM */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LeadsSection data={data.leads} />
            <HsmSection data={data.hsm} />
          </div>

          {/* Row 2: Pipeline full-width */}
          <PipelineSection data={data.pipeline} />

          {/* Row 3: Closed + Activities */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ClosedSection data={data.closed} />
            <ActivitiesSection data={data.activities_by_user} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
